import { ModalBody, ModalCloseButton, ModalContent, ModalHeader, ModalRoot, ModalTitle } from '@cian/ui-kit';
import { useDeviceType } from '@cian/ui-kit/responsive';
import { ArticleParagraph1 } from '@cian/ui-kit/typography';
import { IconActionClose16 } from '@cian/ui-kit-design-tokens/icons';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TJsonQuery } from '../../../../packages/JsonQuery';
import { closeOfferPreview } from '../../../common/actions/closeOfferPreview/closeOfferPreview';
import { fetchHybridSearchData } from '../../../common/actions/fetchHybridSearchData';
import { fetchTags } from '../../../common/actions/filters';
import { closeMobileListing } from '../../../common/actions/listingModal';
import { setNonGeoParameters } from '../../../common/actions/nonGeoParameters';
import { ETestId } from '../../../common/constants';
import { useApplicationContext } from '../../../common/containers/ApplicationContext';
import { LOCAL_STORAGE_IS_MOBILE_FILTERS_WERE_APPLIED } from '../../../common/hooks/useUxFbPoll/constants';
import {
  getLocalStorageFiltersWereAppliedTwoTimes,
  setLocalStorageFiltersApplied,
} from '../../../common/hooks/useUxFbPoll/localStorageUtils';
import { selectIsModalOpen } from '../../../common/selectors/listingModal';
import { selectRegionDisplayName } from '../../../common/selectors/region/selectRegionDisplayName';
import { selectRegions } from '../../../common/selectors/regions';
import { getSearchMeta } from '../../../common/services/getSearchMeta/getSearchMeta';
import { TThunkDispatch } from '../../../common/types/redux';
import { ISearchMeta } from '../../../common/types/searchMeta';
import { buildSubdomainRedirectUrl } from '../../../common/utils/buildSubdomainRedirectUrl';
import { isSubdomainRedirectNeeded } from '../../../common/utils/isSubdomainRedirectNeeded';
import { Chip } from '../../components/RedesignedFilters';
import { trackFilterFromChipsApply } from '../../tracking';
import { RegionSelectContainerModal } from '../RegionSelectContainer';

interface IChangeSubdomainArgs {
  searchMeta: ISearchMeta;
}

const changeSubdomain = async ({ searchMeta }: IChangeSubdomainArgs) => {
  const needSubdomainRedirect = isSubdomainRedirectNeeded(searchMeta.subdomain);
  let hasSubdomainChanged = false;

  if (needSubdomainRedirect) {
    const redirectUrl = buildSubdomainRedirectUrl(searchMeta);
    window.location.assign(redirectUrl);
    hasSubdomainChanged = true;
  }

  return hasSubdomainChanged;
};

export const RegionChipContainer = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const context = useApplicationContext();
  const deviceType = useDeviceType();
  const { logger } = context;

  const regionName = useSelector(selectRegionDisplayName);
  const regions = useSelector(selectRegions);
  const isListingModalOpen = useSelector(selectIsModalOpen);

  const isLocalStorageFiltersWereAppliedTwoTimes = getLocalStorageFiltersWereAppliedTwoTimes(
    LOCAL_STORAGE_IS_MOBILE_FILTERS_WERE_APPLIED,
  );

  const [isOpen, setIsOpen] = React.useState(false);

  const handleFilterOpen = React.useCallback(() => {
    setIsOpen(true);
    dispatch(closeOfferPreview());
    dispatch(closeMobileListing());
  }, [dispatch]);

  const handleFilterApply = React.useCallback(
    async (nextJsonQuery?: TJsonQuery) => {
      // Закроется модалка
      setIsOpen(false);

      // Эта ветка отвечает за кейс, когда модалку закрыли без изменения гео
      if (!nextJsonQuery) {
        return;
      }

      const searchMeta = await getSearchMeta(context, nextJsonQuery);

      trackFilterFromChipsApply({
        queryString: searchMeta.querystring,
        jsonQuery: nextJsonQuery,
        pageType: isListingModalOpen ? 'Listing' : 'Map',
        filterType: 'geo',
      });

      // Поиск станет с геопараметрами (геопараметры сбрасываются кнопкой "искать в этой области")
      dispatch(setNonGeoParameters(false));

      // Поменяется сабдомен, если нужно. При его изменении страница перезагружается
      const hasSubdomainChanged = await changeSubdomain({ searchMeta });

      // Если сабдомен не изменился, вручную передвигаем карту и загружаем кластеры
      if (!hasSubdomainChanged) {
        dispatch(fetchHybridSearchData({ updateBounds: true, deviceType }));
        dispatch(fetchTags());
      }
      //Если фильтр был применен 2 раза, сохраняем значеине в LocalStorage
      if (!isLocalStorageFiltersWereAppliedTwoTimes) {
        setLocalStorageFiltersApplied(LOCAL_STORAGE_IS_MOBILE_FILTERS_WERE_APPLIED);
      }
    },
    [context, deviceType, isLocalStorageFiltersWereAppliedTwoTimes, setLocalStorageFiltersApplied],
  );

  return (
    <ModalRoot open={isOpen} onOpenChange={setIsOpen}>
      <Chip onClick={handleFilterOpen} testId={ETestId.RedesignedFiltersRegionChip}>
        <ArticleParagraph1>{regionName || 'Область карты'}</ArticleParagraph1>
      </Chip>
      <ModalContent>
        <ModalCloseButton title="Закрыть" size="XS" theme="fill_light" icon={<IconActionClose16 />} />
        <ModalHeader>
          <ModalTitle>Введите регион или город</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <RegionSelectContainerModal logger={logger} regions={regions} onClose={handleFilterApply} />
        </ModalBody>
      </ModalContent>
    </ModalRoot>
  );
};
